import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";
import {
  adminDeleteUser,
  adminLookupUser,
  adminResetUser,
  getAdminAudit,
  getAdminCapabilities,
} from "@/server/services/adminService";
import {
  AdminAuditEntry,
  AdminCapabilities,
  AdminLookupResult,
  AdminResetResult,
} from "@/server/types/admin";

const inputClass =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring";
const cardClass = "rounded-lg border border-border bg-card p-5 shadow-sm";
const labelClass = "mb-1 block text-xs font-medium text-muted-foreground";

function errMessage(e: unknown): string {
  // adminService calls all go through requestApi, which never rethrows a raw
  // AxiosError: it throws either `new Error(<plain-text body>)` or an ApiError
  // (also an Error). Anything else is a non-Error throwable.
  if (e instanceof Error) {
    return e.message;
  }
  return "Request failed";
}

const TIERS: Record<number, string> = { 0: "Free", 1: "Pro", 2: "Enterprise" };

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words text-foreground">{value}</dd>
    </div>
  );
}

/**
 * Lookup-first workflow: one search resolves an account, then Reset/Delete act
 * on THAT account behind confirmation dialogs. Because the resolved account is
 * in hand, each dialog can warn with full context up front (e.g. an existing
 * recovery phrase) instead of only reporting it after the fact.
 */
function LookupSection({ onActed }: { onActed: () => void }) {
  const [identifier, setIdentifier] = useState("");
  const [searched, setSearched] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AdminLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // The lookup still in flight, if any. Held so a newer submission can cancel it
  // — and so its aborted signal can veto its state writes.
  const inFlight = useRef<AbortController | null>(null);

  useEffect(() => () => inFlight.current?.abort(), []);

  const doLookup = useCallback(async (target: string) => {
    if (!target) {
      return;
    }
    // Supersede whatever is in flight. Two lookups can overlap (the button is
    // disabled while loading, the Enter handler is not), and without this the
    // slower one can resolve LAST and win — showing account A's details under
    // identifier B, on a panel used to reset and delete accounts. `aborted` is
    // then the authority on ownership: an aborted run writes nothing.
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    const { signal } = controller;

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await adminLookupUser(target, signal);
      if (signal.aborted) {
        return;
      }
      // Remember what was actually searched so the outcome never claims to be
      // about whatever is in the input by the time it renders.
      setSearched(target);
      setResult(r);
    } catch (e) {
      // A superseded or unmounted lookup rejects with the abort — drop it
      // silently instead of flashing a bogus error.
      if (signal.aborted) {
        return;
      }
      setError(errMessage(e));
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const run = useCallback(
    () => doLookup(identifier.trim()),
    [doLookup, identifier],
  );

  return (
    <section className={cardClass}>
      <h2 className="mb-3 text-lg font-semibold text-foreground">
        Find a user
      </h2>
      <label className={labelClass} htmlFor="admin-lookup-identifier">
        Username or email
      </label>
      <div className="flex gap-2">
        <input
          id="admin-lookup-identifier"
          className={inputClass}
          value={identifier}
          placeholder="jane@example.com or janedoe"
          onChange={(e) => {
            setIdentifier(e.target.value);
            // Don't leave the previous account's details (and its Reset/Delete
            // actions) on screen while a different identifier is being typed.
            setResult(null);
            setError(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && run()}
        />
        <Button onClick={run} disabled={loading || !identifier.trim()}>
          {loading ? "Searching…" : "Look up"}
        </Button>
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      {result && !result.exists && (
        <p className="mt-3 text-sm text-muted-foreground">
          No account found for “{searched}”.
        </p>
      )}

      {result && result.exists && (
        <div className="mt-4">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <Field label="Username" value={result.username} />
            <Field label="Email" value={result.email ?? "— (none on record)"} />
            <Field
              label="Recovery phrase"
              value={result.has_recovery_phrase ? "Yes" : "No"}
            />
            <Field
              label="Subscription"
              value={
                result.subscription_tier != null
                  ? (TIERS[result.subscription_tier] ??
                    String(result.subscription_tier))
                  : "—"
              }
            />
            <Field
              label="Verified"
              value={
                result.is_verified == null
                  ? "—"
                  : result.is_verified
                    ? "Yes"
                    : "No"
              }
            />
            <Field
              label="Owns groups"
              value={String(result.owned_group_count)}
            />
            <Field label="Created" value={result.created_at ?? "—"} />
          </dl>

          <div className="mt-4 flex gap-2">
            <Button onClick={() => setResetOpen(true)}>Reset password</Button>
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              Delete user
            </Button>
          </div>

          <ResetDialog
            open={resetOpen}
            account={result}
            onOpenChange={setResetOpen}
            // Reflect the cleared recovery phrase without a refetch so the card
            // can't keep claiming "Yes" after a reset, and refresh the audit log.
            onResetDone={() => {
              setResult((prev) =>
                prev ? { ...prev, has_recovery_phrase: false } : prev,
              );
              onActed();
            }}
          />
          <DeleteDialog
            open={deleteOpen}
            account={result}
            onOpenChange={setDeleteOpen}
            // The account is gone — drop it and the search term with it, and
            // refresh the audit log.
            onDeleted={() => {
              setResult(null);
              setIdentifier("");
              setSearched("");
              onActed();
            }}
          />
        </div>
      )}
    </section>
  );
}

function ResetDialog({
  open,
  account,
  onOpenChange,
  onResetDone,
}: {
  open: boolean;
  account: AdminLookupResult;
  onOpenChange: (o: boolean) => void;
  onResetDone: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AdminResetResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const username = account.username ?? "";

  useEffect(() => () => clearTimeout(copyResetTimer.current), []);

  // Start each open from a clean slate so a fresh dialog never shows a stale
  // password or error from a previous target.
  useEffect(() => {
    if (open) {
      setResult(null);
      setError(null);
      setCopyState("idle");
      clearTimeout(copyResetTimer.current);
    }
  }, [open]);

  const copyPassword = useCallback(async (password: string) => {
    let wrote = false;
    try {
      // navigator.clipboard is undefined on insecure origins; the write itself
      // can also be rejected (permissions, document not focused).
      await navigator.clipboard.writeText(password);
      wrote = true;
    } catch {
      // fall through to the failure state
    }
    setCopyState(wrote ? "copied" : "failed");
    clearTimeout(copyResetTimer.current);
    copyResetTimer.current = setTimeout(() => setCopyState("idle"), 1500);
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminResetUser(username);
      setResult(r);
      onResetDone();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [username, onResetDone]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password for {username}</DialogTitle>
          <DialogDescription>
            Generates a new temporary password and re-issues this account’s
            keys.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3 text-sm">
            {account.has_recovery_phrase && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-foreground">
                <strong>This account has a recovery phrase.</strong> Resetting
                clears it — if the user later finds their old phrase it will no
                longer work. Only do this once they’ve confirmed they can’t
                recover it themselves.
              </div>
            )}
            {account.owned_group_count > 0 && (
              <p className="text-muted-foreground">
                Owns {account.owned_group_count} group(s); those chats stay
                intact and are re-keyed where a recovery helper is a member.
              </p>
            )}
            <p className="text-muted-foreground">
              The user’s notes-to-self chat and any chat no helper is in will
              become unreadable.
            </p>
            {error && <p className="text-destructive">{error}</p>}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={run} disabled={loading}>
                {loading ? "Resetting…" : "Confirm reset"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-border bg-background p-3">
              <div className="text-xs text-muted-foreground">
                Temporary password for {result.username}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <code className="rounded font-mono flex-1 break-all bg-muted px-2 py-1 text-sm text-foreground">
                  {result.temp_password}
                </code>
                <Button
                  variant="outline"
                  onClick={() => void copyPassword(result.temp_password)}
                >
                  {copyState === "copied"
                    ? "Copied"
                    : copyState === "failed"
                      ? "Copy failed"
                      : "Copy"}
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Relay over a trusted channel. Not stored anywhere; it can’t be
                shown again.
              </p>
            </div>
            <p className="text-foreground">
              Re-keyed {result.groups_rewrapped} group(s) and{" "}
              {result.p2p_rewrapped} direct chat(s).
            </p>
            {result.warnings.length > 0 && (
              <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                {result.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  open,
  account,
  onOpenChange,
  onDeleted,
}: {
  open: boolean;
  account: AdminLookupResult;
  onOpenChange: (o: boolean) => void;
  onDeleted: () => void;
}) {
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const username = account.username ?? "";

  // Fresh confirmation each open — a value left from a previous target must not
  // pre-arm the button for a different account.
  useEffect(() => {
    if (open) {
      setConfirm("");
      setError(null);
    }
  }, [open]);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await adminDeleteUser(username, confirm.trim());
      onOpenChange(false);
      onDeleted();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [username, confirm, onDeleted, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {username}?</DialogTitle>
          <DialogDescription>
            This permanently deletes the account and cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {account.owned_group_count > 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-foreground">
              This user owns{" "}
              <strong>{account.owned_group_count} group(s)</strong>, which will
              be deleted along with the account.
            </div>
          )}
          <div>
            <label
              className={labelClass}
              htmlFor="admin-delete-confirm-username"
            >
              Type the username to confirm
            </label>
            <input
              id="admin-delete-confirm-username"
              className={inputClass}
              value={confirm}
              placeholder={username}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          {error && <p className="text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={run}
              disabled={loading || confirm.trim() !== username}
            >
              {loading ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AuditPanel({ refreshKey }: { refreshKey: number }) {
  const [rows, setRows] = useState<AdminAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await getAdminAudit(100));
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return (
    <section className={cardClass}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">
          Recent actions
        </h2>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!error && rows.length === 0 && !loading && (
        <p className="text-sm text-muted-foreground">
          No actions recorded yet.
        </p>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="py-1 pr-4">When</th>
                <th className="py-1 pr-4">Actor</th>
                <th className="py-1 pr-4">Action</th>
                <th className="py-1 pr-4">Target</th>
                <th className="py-1 pr-4">Result</th>
              </tr>
            </thead>
            <tbody className="text-foreground">
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border align-top">
                  <td className="whitespace-nowrap py-1 pr-4 text-muted-foreground">
                    {new Date(r.timestamp).toLocaleString()}
                  </td>
                  <td className="py-1 pr-4">{r.actor}</td>
                  <td className="py-1 pr-4">{r.action}</td>
                  <td className="py-1 pr-4">{r.target}</td>
                  <td className="py-1 pr-4">
                    <span
                      className={
                        r.success ? "text-green-600" : "text-destructive"
                      }
                    >
                      {r.success ? "ok" : "failed"}
                    </span>
                    {r.details ? (
                      <span className="text-muted-foreground">
                        {" "}
                        — {r.details}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [caps, setCaps] = useState<AdminCapabilities | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "denied" | "error">(
    "loading",
  );
  const [auditRefresh, setAuditRefresh] = useState(0);
  const bumpAudit = useCallback(() => setAuditRefresh((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await getAdminCapabilities();
        if (!alive) {
          return;
        }
        setCaps(c);
        setState(c.is_admin || c.is_support ? "ready" : "denied");
      } catch {
        if (alive) {
          setState("error");
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (state === "loading") {
    return (
      <div className="p-8 text-sm text-muted-foreground">Checking access…</div>
    );
  }

  if (state === "denied" || state === "error") {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <h1 className="mb-2 text-xl font-semibold text-foreground">
          Support panel
        </h1>
        <p className="mb-4 text-sm text-muted-foreground">
          {state === "error"
            ? "Could not verify your access. Try again later."
            : "You don’t have access to the support panel."}
        </p>
        <Button onClick={() => navigate("/")}>Back to app</Button>
      </div>
    );
  }

  return (
    // This page is a route of its own, outside ChatLayout, so nothing above it
    // provides a scroll container — and `html` is `overflow: hidden` globally
    // for the fixed three-column chat shell. Own the viewport height and the
    // scrolling here, or the panels below the fold are unreachable on short
    // screens. Bottom safe-area padding keeps the last card clear of the iOS
    // home indicator.
    <div className="h-dvh overflow-y-auto overscroll-contain">
      <div className="mx-auto max-w-3xl space-y-6 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Support panel
            </h1>
            <p className="text-sm text-muted-foreground">
              Signed in as {caps?.username} ·{" "}
              {caps?.is_admin ? "admin" : "support"}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate("/")}>
            Back to app
          </Button>
        </header>

        <LookupSection onActed={bumpAudit} />
        <AuditPanel refreshKey={auditRefresh} />
      </div>
    </div>
  );
}
